/*
Purpose: Authentication wrapper and login/register portal.
Audience: Unauthenticated users.
What it does:
- Presents login/register UI and redirects to app on success
Interplay:
- Works with Firebase Auth; after login, app shows user content
*/
import React, { useState } from 'react';
import '../../styles/AuthPortal.css';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  auth // Import auth from your firebase.js
} from '../../firebase'; // Adjust path if your firebase.js is elsewhere
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';

const AuthPortal = ({ onClose }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSignUpMode, setIsSignUpMode] = useState(false); // To toggle between login/signup
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInWithEmailAndPassword(auth, email, password);
      console.log('Logged In successfully!');
      onClose(); // Close the modal on successful login
    } catch (error) {
      console.error('Error logging in:', error.message);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create user account
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Use the provided display name or extract from email
      const userDisplayName = displayName.trim() || email.split('@')[0];
      
      // Update display name
      await updateProfile(user, { displayName: userDisplayName });
      
      // Create user profile document (Phase 2 schema)
      await setDoc(doc(db, 'userProfiles', user.uid), {
        displayName: userDisplayName,
        email: email,
        role: 'community',
        roleVerified: false,
        casesCreated: 0,
        evidenceSubmitted: 0,
        ordersCreated: 0,
        jobsCompleted: 0,
        totalSpent: 0,
        joinDate: serverTimestamp(),
        bio: '',
        location: '',
        budgetLimits: {
          daily: 10.00,
          monthly: 100.00,
          perAnalysis: 5.00
        },
        collaborationSettings: {
          allowDirectMessages: false,
          shareAnalysisHistory: false,
          expertConsultationAvailable: false
        }
      });

      setSuccess('Account created successfully!');
      
      // Close modal after a short delay to show success message
      setTimeout(() => {
        console.log('Signed Up successfully!');
        window.location.reload(); // Refresh to update UI with new user info
      }, 1500);
    } catch (error) {
      console.error('Error signing up:', error.message);
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay">
      <div className="auth-modal-content">
        <button onClick={onClose} className="auth-modal-close-button">&times;</button>
        <h2>{isSignUpMode ? 'Sign Up' : 'Log In'}</h2>
        
        {error && <div className="auth-error">{error}</div>}
        {success && <div className="auth-success">{success}</div>}
        
        <form className="auth-form" onSubmit={isSignUpMode ? handleSignUp : handleLogin}>
          {isSignUpMode && (
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Display Name"
              className="auth-input"
            />
          )}
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email"
            className="auth-input"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="auth-input"
            required
          />
          <button type="submit" className="auth-button" disabled={loading}>
            {loading ? 'Processing...' : isSignUpMode ? 'Sign Up' : 'Log In'}
          </button>
        </form>
        <p className="auth-toggle-prompt">
          {isSignUpMode ? (
            <>Already have an account? <span onClick={() => setIsSignUpMode(false)}>Log In</span></>
          ) : (
            <>Don't have an account? <span onClick={() => setIsSignUpMode(true)}>Sign Up</span></>
          )}
        </p>
      </div>
    </div>
  );
};

export default AuthPortal; 